/**
 * The connected tenant's tax rates, and the two Xero **Sales defaults** every
 * screen that sets them shares.
 *
 * ## Why this is a module and not two copies
 *
 * These defaults are set in two places that look unrelated: the company form,
 * where they belong to the record, and the pre-push step on a quote, where a
 * missing one is what stops the push. Both need the same live rate list, the
 * same four-way failure model, and the same wording — and the wording here is
 * correctness rather than style (see {@link LINE_AMOUNT_OPTIONS}).
 *
 * This module exists *before* the second copy rather than after it. The company
 * stage shipped as three copies in one afternoon and two of them disagreed;
 * that reconciliation is the cost being avoided, and it is always paid later
 * and by someone else.
 *
 * ## What is NOT here
 *
 * The rendering lives in `components/xero/XeroSalesDefaultsFields.tsx`. This
 * file is the data and the vocabulary; keeping the JSX out of it is what lets
 * a caller lay the two controls out differently — the company form gives them
 * a two-column section, the push step stacks them inside a dialog — without
 * either one forking the logic to do it.
 *
 * ## Which rates a SALES picker may offer
 *
 * `GET /xero/tax-rates` is unfiltered and stays that way — correct for a
 * display list. Every picker in this app writes a *sales* document, where an
 * expense rate is a guaranteed `422`, so the narrowing lives here:
 * {@link sellableTaxRates}, {@link isSalesTaxRate}, {@link expenseRateCount}.
 * This reverses a decision that was written down and shipped; the reasoning and
 * the incident that overturned it are on {@link taxRateHint}.
 *
 * A **line** picker narrows once more, because it writes a percentage as well
 * as a code: {@link lineTaxRates}, {@link hasEffectiveRate},
 * {@link unquantifiedSalesRateCount}. Two lists rather than one because the two
 * pickers write different things — see {@link lineTaxRates} for the table.
 *
 * ## WHERE a line's rate came from — and, once again, whether it may be TYPED
 *
 * Since migration 096 a line's rate can be TAKEN from the company's own
 * `xeroSalesTaxRate`, and `taxRateSource` records that it was.
 * {@link isCompanySourcedTaxRate} answers it and {@link companyTaxRateReason}
 * is the sentence — here rather than in either grid, because the quote grid and
 * the invoice grid both ask, and a sentence held twice is a sentence that
 * disagrees with itself later.
 *
 * 🔴 **THREE POSITIONS IN ONE DAY, 2026-08-30, and the third is the live
 * one.** Written out because a reader meeting only the middle one will "fix"
 * this back, and the middle one is the version this file used to argue at
 * length:
 *
 * 1. **read-only on `'company'`** — the original ruling, gated here.
 * 2. **always editable** — *SUPERSEDED: "Whether a line may set its rate AT ALL
 *    … the rate is editable on every line, and this pair is provenance to show,
 *    never a control to disable."* The stated reason was that a frozen `0.00`
 *    is indistinguishable from a company that genuinely zero-rates.
 * 3. **read-only WHEN the company actually states a rate; editable when it does
 *    not** — the owner's choice, taken with the two above in front of him.
 *
 * ⚠️ **WHAT MAKES 3 SAFE AND DID NOT EXIST AT 1: the per-line Xero tax CODE
 * is EDITABLE.** A rate/code disagreement is `XERO_TAX_RATE_MISMATCH` at push
 * time, and with the code open a person can still resolve it from the document
 * screen — the picker writes the code and its own percentage in ONE patch.
 * Locking both is what stranded them, which is why 2 happened. **Never gate the
 * code on this field**: half a pair is the failure, in either direction.
 *
 * ⚠️ **And 2's objection is answered rather than overruled.** `'company'`
 * means the company STATES a percentage, so a locked figure is never a spurious
 * `0.00`; where the company states nothing the line reports `'line'` and the
 * control stays open. {@link companyTaxRateReason} carries a defensive branch
 * for that pairing failing anyway, and it fails towards EDITABLE.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api';
import type { XeroLineAmountType, XeroTaxRate } from './types';
import {
  XeroCooldownError,
  clearXeroCooldown,
  noteXeroRateLimit,
  xeroCooldownMessage,
  xeroCooldownSecondsRemaining,
} from './xero-cooldown';

/** The noun every failure message in this file uses for the list it reads. */
const SUBJECT = 'tax rates';

/**
 * The three values Xero accepts for a contact's sales line-amount default.
 *
 * `''` is not in this list and is not a fourth member: it is the *absence* of a
 * default, which the API stores as `null` and Xero reads as "use the
 * organisation's settings". That is the first option in Xero's own dropdown and
 * a real answer, so it is offered as labelled text rather than as a blank row.
 *
 * ⚠️ **These labels say what XERO should default to, never how EMERGE
 * calculates.** EMERGE's own arithmetic is tax-exclusive with no inclusive mode
 * at all, so a company can carry `INCLUSIVE` here while every EMERGE figure
 * stays exclusive. A label implying otherwise would leave the two systems
 * printing different totals for one sale with nothing flagging it.
 */
export const LINE_AMOUNT_OPTIONS: readonly { value: XeroLineAmountType; label: string }[] = [
  { value: 'EXCLUSIVE', label: 'Tax exclusive' },
  { value: 'INCLUSIVE', label: 'Tax inclusive' },
  { value: 'NONE', label: 'No tax' },
];

export const USE_ORGANISATION_SETTINGS = 'Use organisation settings';

/**
 * Narrows the stored string back to the union, or to `null`.
 *
 * Anything outside the three is `null` rather than cast through: the control
 * can only produce those three, but the *baseline* comes from the server, and a
 * value the union does not cover must not be smuggled into a write claiming to
 * be one.
 */
export function toLineAmountType(value: string): XeroLineAmountType | null {
  return LINE_AMOUNT_OPTIONS.some((option) => option.value === value)
    ? (value as XeroLineAmountType)
    : null;
}

/**
 * Why the rates could not be read, in terms of **what the reader should do**.
 *
 * `XERO_TOKEN_REJECTED` and `XERO_UPSTREAM_UNAVAILABLE` are both `502` and they
 * need opposite advice — one says re-authorise the connection, the other says
 * nothing is wrong, wait. Branching on `502` would give whichever of those two
 * sentences was written first to both, and the wrong one sends someone either
 * to reconnect a healthy connection or to sit waiting on one that is dead.
 */
export interface TaxRatesFailure {
  /**
   * ⚠️ **`cooldown` is not a flavour of `retry`, and must never be folded into
   * it.** `retry` means "Xero did not answer, try again shortly" — advice that
   * is actively wrong under a rate limit, where trying again spends another
   * request out of a budget that is already empty and cannot succeed until it
   * resets. The two also want different tones: a cooldown is a calm, expected
   * state of a healthy connection, not a fault. See `xero-cooldown.ts`, which
   * owns the suppression this kind reports and shares it with
   * `use-xero-branding-themes.ts`.
   */
  kind: 'reconnect' | 'retry' | 'missing' | 'cooldown' | 'other';
  message: string;
  /**
   * Seconds until Xero's limit resets. Set only on `kind: 'cooldown'`, where it
   * lets a caller phrase the wait its own way — a folded panel has room for a
   * clause where a field has room for the whole sentence.
   */
  retryAfterSeconds?: number;
}

export interface TaxRatesState {
  rates: XeroTaxRate[];
  loading: boolean;
  /**
   * Non-null when the list could not be read.
   *
   * `api.xeroTaxRates()` throws rather than resolving to `[]`, deliberately, so
   * this can never be confused with a tenant that genuinely has no rates. It
   * must always reach the screen: a silently empty dropdown reads as "this
   * organisation has no VAT rates", which is indistinguishable from a real
   * answer and would let someone save a blank believing they had chosen.
   */
  error: TaxRatesFailure | null;
  reload: () => void;
}

export function describeTaxRateFailure(caught: unknown): TaxRatesFailure {
  /*
   * ⚠️ FIRST, before the `instanceof ApiError` gate — a suppressed read is
   * rejected with a `XeroCooldownError`, which is not an `ApiError` and would
   * otherwise fall out of the bottom as "check your connection and try again":
   * the one piece of advice guaranteed to be wrong here, since the connection
   * is healthy and trying again is what emptied the budget.
   *
   * `noteXeroRateLimit` also RECORDS the cooldown. This describer is the single
   * funnel every tax-rate failure passes through, so recording here is what
   * makes the suppression apply to all four consumers — and to the branding
   * themes, which share the same budget — without any of them knowing it exists.
   */
  const cooldown = noteXeroRateLimit(caught);
  if (cooldown !== null) {
    return {
      kind: 'cooldown',
      message: xeroCooldownMessage(SUBJECT, cooldown),
      retryAfterSeconds: cooldown,
    };
  }
  if (!(caught instanceof ApiError)) {
    return {
      kind: 'other',
      message: 'The Xero tax rates could not be reached. Check your connection and try again.',
    };
  }
  if (caught.code === 'XERO_TOKEN_REJECTED') {
    return {
      kind: 'reconnect',
      message:
        'Xero refused our credentials, so its tax rates could not be read. The connection needs re-authorising before a rate can be chosen here.',
    };
  }
  if (caught.code === 'XERO_UPSTREAM_UNAVAILABLE') {
    return {
      kind: 'retry',
      message:
        'Xero did not answer, so its tax rates could not be read. The connection itself is fine — try again shortly.',
    };
  }
  if (caught.status === 404) {
    return {
      kind: 'missing',
      message: 'Tax rates are not set up on the API yet, so a rate cannot be chosen here.',
    };
  }
  if (caught.status === 401 || caught.status === 403) {
    return {
      kind: 'other',
      message:
        'Your account is not permitted to read the Xero tax rates, so a rate cannot be chosen here.',
    };
  }
  return {
    kind: 'other',
    message: caught.message || 'The Xero tax rates could not be read.',
  };
}

/**
 * The connected tenant's tax rates, read once per opening.
 *
 * `enabled` is false while the connection is still being read and whenever
 * there is no connection: there is no tenant to ask, and a request that can
 * only fail would put a red block on a form whose real state is "Xero is not
 * connected" — a different sentence with a different action.
 */
export function useXeroTaxRates(enabled: boolean): TaxRatesState {
  const [rates, setRates] = useState<XeroTaxRate[]>([]);
  const [error, setError] = useState<TaxRatesFailure | null>(null);
  const [nonce, setNonce] = useState(0);
  /*
   * Which attempt has finished, rather than a `loading` flag set from inside
   * the effect. `enabled` flips from false to true when the connection read
   * settles, and a flag would have to be raised synchronously in the effect to
   * cover that — deriving it during render covers it without the extra pass.
   */
  const [settled, setSettled] = useState<number | null>(null);

  // A failed read must be retryable in place. The alternative is telling
  // someone the rates could not be read and offering them nothing but a reload
  // of the whole form, which throws away everything they had typed.
  //
  // ⚠️ Clearing the cooldown is what makes this button honest. Without it a
  // retry during a rate limit would be swallowed by the suppression below and
  // re-render the identical message, which is worse than offering nothing: the
  // reader is told there is something they can do and then nothing happens.
  const reload = useCallback(() => {
    clearXeroCooldown();
    setError(null);
    setNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    /*
     * A known cooldown short-circuits the request — and does it by REJECTING
     * rather than by returning early, so the state below is written by exactly
     * the same `.catch`/`.finally` pair a real refusal would take.
     *
     * Two things fall out of that and both are requirements. The rates stay
     * `[]` with a non-null `error`, which is the branch every consumer already
     * uses to keep a stored code visible and still clearable; and `settled` is
     * still advanced, so the control does not sit on a loading skeleton for a
     * request that was never made.
     */
    const waiting = xeroCooldownSecondsRemaining();
    const read: Promise<XeroTaxRate[]> =
      waiting === null
        ? api.xeroTaxRates(controller.signal)
        : Promise.reject(new XeroCooldownError(waiting));
    read
      .then((data) => {
        if (controller.signal.aborted) return;
        setRates(data);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setRates([]);
        setError(describeTaxRateFailure(caught));
      })
      .finally(() => {
        if (!controller.signal.aborted) setSettled(nonce);
      });
    return () => controller.abort();
  }, [enabled, nonce]);

  return {
    rates: enabled ? rates : [],
    loading: enabled && settled !== nonce,
    error: enabled ? error : null,
    reload,
  };
}

/**
 * One rate, as the option's secondary line.
 *
 * The **code** is here because it is the value actually stored and sent, and
 * because it is not derivable from the label — `VAT on Imports` is
 * `GSTONIMPORTS` on this UK tenant. It also makes the list searchable by code,
 * since the Combobox filters on label and hint together.
 *
 * ⚠️ **Expense rates are excluded from a sales picker, not merely marked.**
 * Xero's own Sales VAT dropdown is one list serving both sales and purchase
 * documents; ours only ever writes a sales document, and on a sales document
 * an expense rate is not a worse choice, it is a **guaranteed refusal**.
 *
 * The incident that proved it: production quote 9, line 1 saved `INPUT2`
 * ("20% (VAT on Expenses)"), and `POST /xero/create/quotes/9` answered
 * `422 XERO_TAX_TYPE_NOT_REVENUE` — *"…is an EXPENSE tax rate and cannot apply
 * to income, but a quote is a sales document. Did you mean OUTPUT2
 * (20% (VAT on Income))?"*. `INPUT2` and `OUTPUT2` are both 20%, both active,
 * and sit one word apart in a list of 15. A marker in a hint does not survive
 * that; only absence does.
 *
 * **The owner's decision is now: a sales picker offers revenue rates only.**
 * See {@link sellableTaxRates}. The endpoint stays unfiltered on purpose — the
 * backend's own note is that an unfiltered list is correct for a DISPLAY list
 * and "a WRITE is a different question" — so the narrowing belongs here, at the
 * one call site that knows it is writing a sales document.
 *
 * The hint still marks the expense case rather than assuming it is unreachable,
 * because {@link sellableTaxRates} deliberately injects a stored expense code
 * back into the list. That row must say what it is; it is the one place a
 * reader meets an expense rate on a sales picker and it is the row explaining
 * why their push is being refused.
 */
export function taxRateHint(rate: XeroTaxRate): string {
  const parts = [rate.taxType];
  /*
   * ⚠️ **`effectiveRate` is nullable and `` `${null}%` `` renders `null%`.** That
   * would print a word where a tax percentage goes, in the one line a reader
   * uses to tell `INPUT2` from `OUTPUT2`.
   *
   * Said in words rather than as `0%`, because `null` means **Xero declined to
   * state a percentage**, not that the percentage is zero — and this hint sits
   * beside rates that genuinely ARE 0% on this tenant (`ZERORATEDOUTPUT`,
   * `EXEMPTOUTPUT`, `NONE`). A `0%` here would make "we do not know" and "no
   * tax is charged" read identically on a document a customer is invoiced
   * against.
   */
  parts.push(
    hasEffectiveRate(rate) ? `${rate.effectiveRate}%` : 'no percentage published by Xero',
  );
  /*
   * ⚠️ THREE states. `canApplyToRevenue` is `boolean | null`, and a two-branch
   * ternary here would print **"expense rate" over a rate Xero simply declined
   * to classify** — a claim
   * we invented, on the one hint whose job is to stop `INPUT2` being picked
   * where `OUTPUT2` was meant.
   *
   * Said in words for the same reason the percentage is: "we do not know" and
   * "Xero says no" must not read identically. Same shape as
   * `hasEffectiveRate` immediately above — `null` is not the falsy member of a
   * pair, it is the absence of an answer.
   */
  if (rate.canApplyToRevenue === true) parts.push('applies to revenue');
  else if (rate.canApplyToRevenue === false) parts.push('expense rate');
  else parts.push('not classified by Xero');
  if (rate.status && rate.status.toUpperCase() !== 'ACTIVE') parts.push(rate.status.toLowerCase());
  return parts.join(' · ');
}

/**
 * How many of the tenant's rates may be used on a sales document.
 *
 * Surfaced as a footnote. See {@link taxRateHint} and {@link sellableTaxRates}
 * for the filter itself; two footnotes count against this function.
 */
export function revenueRateCount(rates: readonly XeroTaxRate[]): number {
  return rates.filter(isSalesTaxRate).length;
}

/**
 * Whether this rate may be used on a sales document at all.
 *
 * A quote and an invoice are both sales documents, so this is the whole test —
 * there is no per-document variation to parameterise. Reads
 * `canApplyToRevenue` and nothing else: **never match on `taxType` or `name`**,
 * which is how `INPUT2` and `OUTPUT2` get confused in the first place. The
 * tenant's codes are unconstrained strings an accountant can add to, and a
 * label carries no reliable signal either — `VAT on Imports` is `GSTONIMPORTS`
 * on this UK tenant, which names a tax it does not have.
 */
export function isSalesTaxRate(rate: XeroTaxRate): boolean {
  /*
   * ⚠️ `=== true`, not truthiness, now that this is `boolean | null`. The two
   * behave identically today — `null` is falsy — and the explicit test is here
   * so that the THIRD state is visibly decided rather than accidentally
   * inherited from JavaScript.
   *
   * `null` is excluded on purpose: the backend's push guard fails closed on it
   * too, so offering such a rate would offer one the push then refuses. That is
   * the opposite call from {@link taxRateHint}, which must not LABEL `null` —
   * withholding a choice is recoverable and asserts nothing, while a wrong
   * label is a statement a person acts on.
   */
  return rate.canApplyToRevenue === true;
}

/**
 * Did Xero actually state this rate's percentage?
 *
 * ⚠️ **`effectiveRate: null` means Xero DECLINED TO SAY, never that the rate is
 * zero**, and this tenant lists three rates that genuinely are 0%
 * (`ZERORATEDOUTPUT`, `EXEMPTOUTPUT`, `NONE`) — so the two are not
 * distinguishable once collapsed. `effectiveRate ?? 0` would write a 0% tax
 * rate onto a line of a document a customer is invoiced against, and both
 * screens would look completely correct. The backend states the same rule from
 * its side, on the push-time rate check it built: a rate with no
 * `EffectiveRate` is left **unchecked**, because null is not a figure to
 * compare against.
 *
 * **A type predicate on purpose.** The number is only ever needed by a caller
 * that derives `taxRate` from it, and that caller must not reach it by
 * asserting. Narrowing here makes the compiler ask the question instead of a
 * reviewer — the same reason `offersInclusions` has no default in this file's
 * neighbours.
 *
 * Nothing in the live tenant is in this state today (15 active rates, all
 * quantified), so every branch below it is latent. It stops being
 * latent the first time an accountant adds a rate Xero will not quantify, and
 * the failure without this test is a `TypeError` on `null.toFixed` that takes
 * the whole line grid down.
 */
export function hasEffectiveRate(
  rate: XeroTaxRate,
): rate is XeroTaxRate & { effectiveRate: number } {
  return rate.effectiveRate !== null;
}

/**
 * Do two tax codes name the same rate?
 *
 * Case-insensitive, because the server normalises casing on write, so what a
 * record stores and what the tenant currently lists can differ in case while
 * meaning one rate.
 *
 * ⚠️ **EXPORTED, and every comparison of two tax codes must call it — a bare
 * `===` on two tax codes is a bug every time.** A call site that once compared
 * with `===` instead was the source of a real incident:
 * `QuoteLineItemsTable`'s "no longer listed by Xero" injection tested a
 * **live** tenant spelling against a **stored** one with a bare `===`, so
 * `output2` vs `OUTPUT2` double-injected: a phantom option carrying a false
 * "no longer listed by Xero" hint, which `Combobox` then SELECTED (it matches
 * on `option.value === value`), so the trigger showed a bare code and choosing
 * it wrote `xeroTaxType` without `taxRate`.
 *
 * The rule: **close the class, not the instance.** Four
 * sites compare a stored tax code against a live one, and there is now exactly
 * one function that says what "the same code" means. Anything comparing two tax
 * codes must call this rather than reimplementing it — a `===` on these two
 * strings is a bug every time, and it is invisible until a tenant's casing
 * drifts.
 */
export function sameTaxCode(a: string, b: string): boolean {
  return a.toUpperCase() === b.toUpperCase();
}

/**
 * The rates a sales-document picker may offer, with any stored value preserved.
 *
 * Two things at once, and both are required:
 *
 * 1. **Every revenue rate, in the order given.** The endpoint's order is
 *    meaningful — it is the tenant's own — so nothing is sorted here. Of the 15
 *    rates this tenant lists, 7 survive; the 8 expense ones can never be saved
 *    to a Xero quote or invoice and are guaranteed `422
 *    XERO_TAX_TYPE_NOT_REVENUE` on push.
 * 2. **The stored code, injected FIRST, when it names an expense rate the
 *    tenant still lists.** This is the stored-value rule every picker in this
 *    app follows, and here it is load-bearing rather than tidy: production
 *    quote 9's line held `INPUT2` until minutes before this was written. Drop it
 *    and the control renders blank, which reads as "no tax rate chosen" — so
 *    the next save either writes nothing (leaving the bad code stored and
 *    invisible) or clears a value nobody decided to clear. Neither is a
 *    decision anyone made. First rather than in place because it is the row the
 *    reader is looking for, and {@link taxRateHint} marks it "expense rate".
 *
 * ⚠️ **A `storedCode` naming NOTHING in `rates` injects nothing, deliberately.**
 * That is a different case — a code Xero no longer lists at all — and the call
 * sites already own it with their own "no longer listed by Xero" branch, which
 * can say so because it has the code and this function would not. Do not add a
 * second injection here for it: two places synthesising a missing option is how
 * one row renders twice, or renders with the wrong label.
 *
 * `storedCode` of `''` means no stored value; it is not a code and matches
 * nothing.
 *
 * ⚠️ **The lookup is CASE-INSENSITIVE on purpose. Do not "tidy" it back to
 * `===`.** The server normalises a tax code's casing on write — `CompanyDetailPage`
 * records the same thing on its side — so a stored string and the live tenant's
 * string can legitimately differ in case while naming one rate. A case-sensitive
 * lookup here reaches the exact failure this injection exists to prevent, by a
 * route that looks like neither branch working: a stored **expense** code
 * spelled differently is missed here, while the call sites' separate "no longer
 * listed by Xero" fallback correctly declines to fire — because they compare
 * case-insensitively and the code *is* listed. Neither path injects, the option
 * vanishes, the field renders blank over a real stored value, and the next save
 * clears it. Both comparisons must be the same shape or the two halves leave a
 * gap between them.
 *
 * **The injected row carries the LIVE tenant's spelling, not the stored one**,
 * because it is the live rate object itself. That spelling is what goes back out
 * on a write, and it is the one Xero currently lists — echoing a stale casing
 * back would re-store a string the tenant no longer uses, for no gain. The
 * stored spelling still resolves to this row, since the call sites match the
 * field's value case-insensitively too.
 */
export function sellableTaxRates(
  rates: readonly XeroTaxRate[],
  storedCode: string,
): XeroTaxRate[] {
  const sellable = rates.filter(isSalesTaxRate);
  if (!storedCode) return sellable;
  const stored = rates.find((rate) => sameTaxCode(rate.taxType, storedCode));
  /*
   * Already in `sellable` when it is a revenue rate — injecting would duplicate
   * it, and duplicate a React key with it. The injection is only ever for the
   * expense case.
   *
   * Note this test is on the FOUND rate, not a second string comparison: it asks
   * the same object the lookup returned whether it is already in the list, so it
   * cannot fall out of step with how that lookup matched. A second `===` here
   * would have been the case-sensitivity bug again, one line down.
   */
  if (!stored || isSalesTaxRate(stored)) return sellable;
  return [stored, ...sellable];
}

/**
 * The rates a **line-item** picker may offer — {@link sellableTaxRates}, minus
 * any rate Xero has not quantified.
 *
 * ## Why this is a second list and not a stricter `sellableTaxRates`
 *
 * The two pickers ask different questions, and answering both with one list
 * would be wrong in one direction or the other:
 *
 * | picker | writes | may offer a rate with no percentage? |
 * | --- | --- | --- |
 * | a company's Xero **Sales default** | `xeroSalesTaxType` — the CODE alone | **yes** — a valid code is a valid default |
 * | a quote/invoice **line** | `xeroTaxType` **and** `taxRate`, in one patch | **no** — there is no number to write |
 *
 * So this is not "the same rule, tightened". Narrowing `sellableTaxRates`
 * itself would withhold a legitimate choice from the company form, and — worse
 * — would silently corrupt the two footnotes counted with
 * {@link revenueRateCount} and {@link expenseRateCount}, which describe what
 * the ORGANISATION holds rather than what a picker shows.
 *
 * ⚠️ **The failure this prevents is not a bad option, it is a bad WRITE.** The
 * line control writes both keys in one patch, deriving `taxRate` from
 * `effectiveRate`. Handed a `null` it would either throw
 * (`null.toFixed` — the whole grid goes down) or, if someone "fixed" that with
 * `?? 0`, record **0% tax on a customer's document** off a rate Xero refused to
 * state. Both are reachable from one dropdown row, so the row is not offered.
 *
 * **The stored value is still preserved**, exactly as in
 * {@link sellableTaxRates} and for the same reason: a control rendering blank
 * over a real stored code reads as "no tax rate chosen", and the next save
 * either leaves the bad code invisible or clears a value nobody decided to
 * clear. It is hoisted to the front so the "choose a rate below" wording at the
 * call site stays true.
 *
 * Composed from {@link sellableTaxRates} rather than re-filtering `rates`, so
 * the sales rule and its injection stay in one place and this function owns
 * only its own increment.
 */
export function lineTaxRates(
  rates: readonly XeroTaxRate[],
  storedCode: string,
  allowedCodes: ReadonlySet<string> | null = null,
): XeroTaxRate[] {
  /*
   * Owner's decision 2026-10-12: a document line offers ONLY the tax types set
   * on the billing-region defaults. `null` (not read yet, read failed, or no
   * region names a tax type) narrows nothing — a line with no choosable rate at
   * all is worse than a wide list. The stored code always survives, so a line
   * already on another code does not render blank.
   */
  const offered = sellableTaxRates(rates, storedCode).filter(
    (rate) =>
      allowedCodes === null ||
      allowedCodes.has(rate.taxType.trim().toUpperCase()) ||
      sameTaxCode(rate.taxType, storedCode),
  );
  const quantified: XeroTaxRate[] = offered.filter(hasEffectiveRate);
  /*
   * The one unquantified rate that may still be shown: the code this line
   * already holds. `storedCode` of `''` is not a code and matches nothing, so
   * the empty case needs no branch of its own.
   */
  const storedUnquantified = offered.find(
    (rate) => !hasEffectiveRate(rate) && sameTaxCode(rate.taxType, storedCode),
  );
  return storedUnquantified ? [storedUnquantified, ...quantified] : quantified;
}

/**
 * How many of the tenant's SALES rates carry no percentage from Xero.
 *
 * Exists so the line picker's footnote can stay truthful once
 * {@link lineTaxRates} withholds them — a list that silently shrinks is
 * indistinguishable from a failed read, which is the argument
 * {@link expenseRateCount} already makes.
 *
 * ⚠️ **Counts sales rates only, so it never double-counts with
 * {@link expenseRateCount}.** An expense rate with no percentage is withheld
 * for the reason a reader can act on — Xero refuses it on a sales document —
 * and is reported once, there.
 */
export function unquantifiedSalesRateCount(rates: readonly XeroTaxRate[]): number {
  return rates.filter((rate) => isSalesTaxRate(rate) && !hasEffectiveRate(rate)).length;
}

/**
 * How many of the tenant's rates a sales picker is withholding.
 *
 * The complement of {@link revenueRateCount}, and it exists so a picker can
 * SAY so. A list that silently shrinks from 15 to 7 is indistinguishable from a
 * failed read or a half-configured organisation, and someone hunting the 20%
 * rate they use every day — which on this tenant is genuinely there, as
 * `OUTPUT2` — has no way to tell "we hid it" from "it is gone".
 *
 * Counts the whole list, not what {@link sellableTaxRates} returned: an
 * injected stored expense rate is still one of the withheld ones, and it is on
 * screen precisely because it should not have been chosen.
 */
export function expenseRateCount(rates: readonly XeroTaxRate[]): number {
  return rates.filter((rate) => !isSalesTaxRate(rate)).length;
}

/**
 * Whether a line's tax rate was **TAKEN FROM THE COMPANY** — release
 * `20260829123506-c323947`.
 *
 * ⚠️ *SUPERSEDED, and the migration number was wrong: "**migration 096**".*
 * **096 is the region split.** The company column arrived in
 * `092_add_companies_sales_tax_rate.sql`, and the SEEDING is not SQL at all —
 * `quote-products.service.ts` and `invoice-products.service.ts` each resolve a
 * created line as `input.taxRate ?? companyRate ?? undefined`, in TypeScript,
 * at request time. Both halves of that matter to a reader checking this: 096
 * says nothing either way, and there is no DDL default or trigger to find.
 * Corrected 11 September 2026; the same wrong number stood in both line grids
 * and went with it.
 *
 * 🔴 **IT GATES THE PERCENTAGE AGAIN, AND ONLY THE PERCENTAGE — owner,
 * third position of 2026-08-30.** *SUPERSEDED, and it is the version a reader
 * is most likely to have met: "IT MUST NOT GATE A CONTROL. THE OWNER REVERSED
 * THAT ON 2026-08-30 … The rate — and the Xero tax CODE beside it — are
 * editable on every line whatever this answers … Never re-lock one 'for
 * safety'."* The sequence is read-only, then always editable, then read-only
 * where the company states a rate; the module header has all three with the
 * reasons, and only the third is live.
 *
 * `true` means the company states a rate and this line holds it, so the
 * PERCENTAGE box is not offered and the figure is changed on the company
 * record. `false` — which includes `'line'`, meaning the company states
 * nothing — leaves everything open.
 *
 * ⚠️ **THE XERO TAX CODE IS NOT GATED ON THIS, EVER.** That is the whole
 * reason position 3 is safe where position 1 was not: editing the percentage
 * while the code stays put produces `XERO_TAX_RATE_MISMATCH` at push time, and
 * an open code picker — which writes the code and its own percentage in ONE
 * patch — is the only way to clear that from a line grid. Locking both is what
 * produced position 2. Never "harmonise" the pair by shutting the second half.
 *
 * ⚠️ **`=== 'company'`, written out, and never a truthiness test.** The field
 * is a non-empty string in BOTH states, so `if (line.taxRateSource)` answers
 * `true` for every line in the database — and a hint that appears everywhere
 * says nothing. That explicit comparison is the whole point of the function;
 * there is nothing else in it.
 *
 * `undefined` — a draft row no server has ruled on yet — resolves to `false`:
 * nothing is known about where its rate came from, so nothing is claimed, and
 * an unsaved row is never locked on a provenance nobody has stated.
 */
export function isCompanySourcedTaxRate(source: 'company' | 'line' | undefined): boolean {
  return source === 'company';
}

/**
 * WHERE this line's tax rate came from, and what it is showing — ONE copy, read
 * by the quote grid and the invoice grid, which is the pair this module exists
 * to keep from wording the same fact two ways.
 *
 * 🔴 **IT IS A `disabledReason` AGAIN — owner, third position of
 * 2026-08-30.** *SUPERSEDED: "IT IS A HINT, NOT A `disabledReason`, SINCE
 * 2026-08-30 … Nothing is read-only, so the sentence stopped explaining a
 * refusal and started explaining a VALUE … It must not read as a prohibition,
 * or an editable control will look broken."* The percentage IS read-only on a
 * company-sourced line now, so the sentence explains a refusal again — and,
 * because a disabled control with no explanation is indistinguishable from a
 * broken one, it names the company as the source AND both ways out.
 *
 * ⚠️ **It carries the RATE, and that half is correctness rather than courtesy.**
 * With a Xero code chosen the picker's trigger reads the code's NAME and the
 * percentage appears nowhere in the cell. The rate is passed through as the
 * decimal STRING it is: never parsed, rounded or reformatted.
 *
 * ⚠️ **`0.00%` here is a rate somebody CHOSE, never an absence.** The sentence
 * says where the figure came from rather than what it amounts to, so it cannot
 * be read as "no tax" — the claim this repo has already made wrongly three times
 * off a value it should not have been reading.
 *
 * **One clause, and it stays one clause** — it renders as a `title` in the
 * desktop grid, where nothing explanatory may sit inside a cell.
 */
export function companyTaxRateReason(taxRate: string): string {
  const rate = taxRate.trim();
  /*
   * ⚠️ **THE DEFENSIVE BRANCH, AND IT MUST NEVER CLAIM A COMPANY RATE.**
   * `'company'` is supposed to mean the company STATES a percentage — the
   * server reports `'line'` when it does not — so this is believed unreachable.
   * It is written anyway because a control locked over a blank figure, or over
   * a `0.00` nobody chose, is exactly the ambiguity position 2 existed to
   * prevent. **Both grids fail towards EDITABLE here**: the lock is keyed on
   * the figure being PRESENT rather than on the flag alone, and this wording
   * says the rate is still the line's rather than implying a refusal.
   */
  if (rate === '') {
    return 'Seeded from the company record, which states no percentage — this line’s rate is yours to set.';
  }
  /*
   * ⚠️ Names the source AND both ways out, because a disabled box with no
   * route forward reads as broken rather than as refused. The second way out is
   * the mitigation the whole ruling rests on: the Xero rate picker writes the
   * code and its percentage in one patch, so it is the one route that cannot
   * leave the two disagreeing.
   */
  return `${rate}% — set on the company record. Change it there, or choose a different Xero tax rate on this line.`;
}

/*
 * One shared read for every row of every grid on the page, refreshed after a
 * minute so an edit on /catalog reaches a document without a full reload.
 */
const DOCUMENT_TAX_CODES_TTL_MS = 60_000;
let documentTaxCodesCache: {
  at: number;
  promise: Promise<ReadonlySet<string> | null>;
} | null = null;

function readDocumentTaxCodes(): Promise<ReadonlySet<string> | null> {
  const now = Date.now();
  if (documentTaxCodesCache && now - documentTaxCodesCache.at < DOCUMENT_TAX_CODES_TTL_MS) {
    return documentTaxCodesCache.promise;
  }
  const promise = api
    .listBillingRegionDefaults()
    .then((envelope) => {
      const codes = new Set<string>();
      for (const row of envelope.data) {
        const code = row.xeroTaxType?.trim().toUpperCase();
        if (code) codes.add(code);
      }
      return codes.size > 0 ? codes : null;
    })
    .catch(() => {
      // A failed read narrows nothing; forget it so the next mount retries.
      documentTaxCodesCache = null;
      return null;
    });
  documentTaxCodesCache = { at: now, promise };
  return promise;
}

/**
 * The tax codes a quote, invoice or credit-note LINE may offer: the distinct
 * Xero tax types set on the billing-region defaults (owner, 2026-10-12). Feed
 * it to {@link lineTaxRates}.
 *
 * `null` means narrow nothing — still loading, the read failed, or no region
 * names a tax type. ⛔ Not for the company form or the billing-region editor:
 * the editor is where this set is chosen, so narrowing it would lock the set.
 */
export function useDocumentTaxCodes(): ReadonlySet<string> | null {
  const [codes, setCodes] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    let live = true;
    void readDocumentTaxCodes().then((result) => {
      if (live) setCodes(result);
    });
    return () => {
      live = false;
    };
  }, []);
  return codes;
}
